import { WorkDefinitionDto } from '@zopup/zop-types';

const daysFromNow = (days: number): Date => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date;
};

// ids for the Manufacturing / Assembly / Refurbishment definitions intentionally match the
// `workDefinitionId` values already used by the mock Work Orders (`../work-orders/data.ts`) and
// mock Work Plans (`../work-plans/data.ts`) so those screens resolve a friendly definition instead
// of a dangling id.
export const generateMockWorkDefinitions = (): WorkDefinitionDto[] => [
    {
        id: 'bom-gear-01-r5',
        code: 'MFG-GEAR-X',
        revision: '05',
        workOrderType: 'Manufacturing',
        status: 'Active',
        applicability: { plantCode: 'BLR-PLANT', priority: 100, effectiveFrom: daysFromNow(-120) },
        bom: { id: 'BOM-GEAR-X', revision: '08' },
        routing: { id: 'ROUTE-GEAR-X', revision: '04' },
        procedureId: 'PROC-GEAR-ASSY',
        policyCode: 'STANDARD_MANUFACTURING_POLICY',
        activatedAt: daysFromNow(-118),
        version: 5,
    },
    {
        id: 'bom-gear-01-r4',
        code: 'MFG-GEAR-X',
        revision: '04',
        workOrderType: 'Manufacturing',
        status: 'Superseded',
        applicability: { plantCode: 'BLR-PLANT', priority: 100, effectiveFrom: daysFromNow(-300) },
        bom: { id: 'BOM-GEAR-X', revision: '07' },
        routing: { id: 'ROUTE-GEAR-X', revision: '03' },
        procedureId: 'PROC-GEAR-ASSY',
        policyCode: 'STANDARD_MANUFACTURING_POLICY',
        activatedAt: daysFromNow(-298),
        version: 4,
    },
    {
        id: 'bom-ctrl-panel-r2',
        code: 'MFG-CTRL-PANEL',
        revision: '02',
        workOrderType: 'Manufacturing',
        status: 'Active',
        applicability: { plantCode: 'BLR-PLANT', priority: 100, effectiveFrom: daysFromNow(-90) },
        bom: { id: 'BOM-CTRL-PANEL', revision: '03' },
        routing: { id: 'ROUTE-CTRL-PANEL', revision: '02' },
        procedureId: 'PROC-CTRL-PANEL-ASSY',
        policyCode: 'STANDARD_MANUFACTURING_POLICY',
        activatedAt: daysFromNow(-88),
        version: 2,
    },
    {
        id: 'bom-conveyor-belt-r1',
        code: 'ASM-CONVEYOR-BELT',
        revision: '01',
        workOrderType: 'Assembly',
        status: 'Active',
        applicability: { plantCode: 'BLR-PLANT', priority: 100, effectiveFrom: daysFromNow(-60) },
        bom: { id: 'BOM-CONVEYOR-BELT', revision: '02' },
        routing: { id: 'ROUTE-CONVEYOR-BELT', revision: '01' },
        policyCode: 'STANDARD_ASSEMBLY_POLICY',
        activatedAt: daysFromNow(-58),
        version: 1,
    },
    {
        id: 'bom-hyd-cylinder-r1',
        code: 'MFG-HYD-CYLINDER',
        revision: '01',
        workOrderType: 'Manufacturing',
        status: 'Active',
        applicability: { plantCode: 'BLR-PLANT', priority: 100, effectiveFrom: daysFromNow(-45) },
        bom: { id: 'BOM-HYD-CYLINDER', revision: '01' },
        routing: { id: 'ROUTE-HYD-CYLINDER', revision: '01' },
        policyCode: 'STANDARD_MANUFACTURING_POLICY',
        activatedAt: daysFromNow(-44),
        version: 1,
    },
    {
        id: 'bom-steel-frame-r1',
        code: 'MFG-STEEL-FRAME',
        revision: '01',
        workOrderType: 'Manufacturing',
        status: 'Active',
        applicability: { plantCode: 'PUNE-PLANT', priority: 100, effectiveFrom: daysFromNow(-200) },
        bom: { id: 'BOM-STEEL-FRAME', revision: '01' },
        routing: { id: 'ROUTE-STEEL-FRAME', revision: '01' },
        policyCode: 'STANDARD_MANUFACTURING_POLICY',
        activatedAt: daysFromNow(-198),
        version: 1,
    },
    {
        id: 'bom-packaging-box-r1',
        code: 'MFG-PACKAGING-BOX',
        revision: '01',
        workOrderType: 'Manufacturing',
        status: 'Active',
        applicability: { plantCode: 'PUNE-PLANT', priority: 100, effectiveFrom: daysFromNow(-250) },
        bom: { id: 'BOM-PACKAGING-BOX', revision: '01' },
        policyCode: 'STANDARD_MANUFACTURING_POLICY',
        activatedAt: daysFromNow(-248),
        version: 1,
    },
    {
        id: 'bom-weld-fixture-r1',
        code: 'RFB-WELD-FIXTURE',
        revision: '01',
        workOrderType: 'Refurbishment',
        status: 'Active',
        applicability: { plantCode: 'BLR-PLANT', priority: 100, effectiveFrom: daysFromNow(-150) },
        routing: { id: 'ROUTE-WELD-FIXTURE', revision: '01' },
        procedureId: 'PROC-WELD-FIXTURE-REFURB',
        policyCode: 'STANDARD_REFURBISHMENT_POLICY',
        activatedAt: daysFromNow(-148),
        version: 1,
    },

    // Asset & Equipment Work — Maintenance / Repair / Calibration / Inspection / Service
    {
        id: 'wd-pm-cnc-500',
        code: 'PM-CNC-500',
        revision: '04',
        workOrderType: 'Maintenance',
        status: 'Active',
        applicability: { plantCode: 'BLR-PLANT', locationCode: 'MACHINING-LINE-A', priority: 100, effectiveFrom: daysFromNow(-180) },
        routing: { id: 'ROUTE-CNC-PM-500', revision: '02' },
        procedureId: 'CNC-PM-PROCEDURE',
        policyCode: 'STANDARD_MAINTENANCE_POLICY',
        activatedAt: daysFromNow(-178),
        version: 4,
    },
    {
        id: 'wd-pm-cnc-monthly',
        code: 'PM-CNC-MONTHLY',
        revision: '02',
        workOrderType: 'Maintenance',
        status: 'Active',
        applicability: { plantCode: 'BLR-PLANT', locationCode: 'MACHINING-LINE-A', priority: 90, effectiveFrom: daysFromNow(-180) },
        procedureId: 'CNC-MONTHLY-CHECKLIST',
        policyCode: 'STANDARD_MAINTENANCE_POLICY',
        activatedAt: daysFromNow(-178),
        version: 2,
    },
    {
        id: 'wd-repair-cnc-spindle',
        code: 'RPR-CNC-SPINDLE',
        revision: '01',
        workOrderType: 'Repair',
        status: 'Active',
        applicability: { plantCode: 'BLR-PLANT', priority: 100, effectiveFrom: daysFromNow(-90) },
        procedureId: 'CNC-SPINDLE-REPAIR-GUIDE',
        policyCode: 'STANDARD_REPAIR_POLICY',
        activatedAt: daysFromNow(-89),
        version: 1,
    },
    {
        id: 'wd-cal-pg-01',
        code: 'CAL-PG-01',
        revision: '03',
        workOrderType: 'Calibration',
        status: 'Active',
        applicability: { plantCode: 'BLR-PLANT', locationCode: 'UTILITY-ROOM', priority: 100, effectiveFrom: daysFromNow(-365) },
        procedureId: 'PG-CAL-PROC',
        policyCode: 'EXTERNAL_LAB_CALIBRATION_POLICY',
        activatedAt: daysFromNow(-360),
        version: 3,
    },
    {
        id: 'wd-insp-incoming-qc',
        code: 'INSP-INCOMING-QC',
        revision: '02',
        workOrderType: 'Inspection',
        status: 'Active',
        applicability: { plantCode: 'PUNE-PLANT', priority: 100, effectiveFrom: daysFromNow(-200) },
        procedureId: 'INCOMING-QC-CHECKLIST',
        policyCode: 'STANDARD_INSPECTION_POLICY',
        activatedAt: daysFromNow(-198),
        version: 2,
    },
    {
        id: 'wd-svc-hvac-quarterly',
        code: 'SVC-HVAC-Q',
        revision: '01',
        workOrderType: 'Service',
        status: 'Active',
        applicability: { plantCode: 'BLR-PLANT', priority: 80, effectiveFrom: daysFromNow(-120) },
        procedureId: 'HVAC-QUARTERLY-SERVICE',
        policyCode: 'STANDARD_SERVICE_POLICY',
        activatedAt: daysFromNow(-118),
        version: 1,
    },

    // A draft, not-yet-activated definition — exercises the "Activate it before relying on it" warning.
    {
        id: 'wd-mfg-gearbox-next',
        code: 'MFG-GEARBOX-NEXT',
        revision: '01',
        workOrderType: 'Manufacturing',
        status: 'Draft',
        applicability: { plantCode: 'BLR-PLANT', priority: 100, effectiveFrom: daysFromNow(0) },
        bom: { id: 'BOM-GEARBOX-NEXT', revision: '01' },
        routing: { id: 'ROUTE-GEARBOX-NEXT', revision: '01' },
        policyCode: 'STANDARD_MANUFACTURING_POLICY',
        version: 1,
    },
];
